import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["scoreOptions", "rangeOptions", "selectOptions", "multiselectOptions", "booleanOptions", "entityOptions", "valuesList", "valuesInput", "valueInput", "multiselectValuesList", "multiselectValuesInput", "multiselectValueInput"]

  connect() {
    this.toggleOptions()
    if (this.hasValuesInputTarget) {
      this.initializeValues()
    }
    if (this.hasMultiselectValuesInputTarget) {
      this.initializeMultiselectValues()
    }
    // Pernkopf 2026-08-20: Sicherheitsnetz — vor jedem Form-Submit hidden Field
    // synchronisieren, damit auch bei Direct-Submit ohne blur/change der
    // aktuelle Text der Value-Inputs uebernommen wird.
    const form = this.element.closest('form')
    if (form) {
      this._boundBeforeSubmit = () => {
        if (this.hasValuesInputTarget && this.hasValueInputTarget) this.updateValues()
        if (this.hasMultiselectValuesInputTarget && this.hasMultiselectValueInputTarget) this.updateMultiselectValues()
      }
      form.addEventListener('submit', this._boundBeforeSubmit, { capture: true })
    }
  }

  disconnect() {
    if (this._boundBeforeSubmit) {
      const form = this.element.closest('form')
      if (form) form.removeEventListener('submit', this._boundBeforeSubmit, { capture: true })
    }
  }

  initializeValues() {
    const valuesInput = this.valuesInputTarget
    if (!valuesInput.value) return

    try {
      // Versuche die Werte aus dem versteckten Input zu laden
      const values = JSON.parse(valuesInput.value)
      if (Array.isArray(values)) {
        // Lösche bestehende Werte
        this.valuesListTarget.innerHTML = ''
        // Füge jeden Wert als neues Input-Feld hinzu
        values.forEach(value => this.addValueWithContent(value))
      }
    } catch (e) {
      // Fehler beim Parsen der Werte
    }
  }

  toggleOptions() {
    const fieldTypeSelect = this.element.querySelector('select[name="custom_property_definition[field_type]"]')
    if (!fieldTypeSelect) return

    const fieldType = fieldTypeSelect.value
    
    // Hide all option sections first
    this.hideAllOptions()
    
    // Show specific options based on field type
    if (fieldType === 'score') {
      this.showScoreOptions()
    } else if (fieldType === 'range') {
      this.showRangeOptions()
    } else if (fieldType === 'select') {
      this.showSelectOptions()
    } else if (fieldType === 'boolean') {
      this.showBooleanOptions()
    } else if (fieldType === 'multiselect') {
      this.showMultiselectOptions()
    } else if (fieldType === 'entity') {
      this.showEntityOptions()
    }
  }

  hideAllOptions() {
    if (this.hasScoreOptionsTarget) {
      this.scoreOptionsTarget.classList.add('hidden')
    }
    if (this.hasRangeOptionsTarget) {
      this.rangeOptionsTarget.classList.add('hidden')
    }
    if (this.hasSelectOptionsTarget) {
      this.selectOptionsTarget.classList.add('hidden')
    }
    if (this.hasBooleanOptionsTarget) {
      this.booleanOptionsTarget.classList.add('hidden')
    }
    if (this.hasMultiselectOptionsTarget) {
      this.multiselectOptionsTarget.classList.add('hidden')
    }
    if (this.hasEntityOptionsTarget) {
      this.entityOptionsTarget.classList.add('hidden')
    }
    // Pernkopf 2026-09-04: select- und multiselect-Block rendern BEIDE ein
    // hidden Input mit Namen custom_property_definition[options][values].
    // Wenn beide aktiv im Form sind, gewinnt der letztgerenderte (multiselect,
    // immer leer "[]") beim Submit -> Auswahl-Werte gehen verloren.
    // Fix: inaktive hidden Inputs disablen; enabled wird im show*Options-Aufruf
    // wieder gesetzt.
    if (this.hasValuesInputTarget) this.valuesInputTarget.disabled = true
    if (this.hasMultiselectValuesInputTarget) this.multiselectValuesInputTarget.disabled = true
  }

  showScoreOptions() {
    if (this.hasScoreOptionsTarget) {
      this.scoreOptionsTarget.classList.remove('hidden')
    }
  }

  showRangeOptions() {
    if (this.hasRangeOptionsTarget) {
      this.rangeOptionsTarget.classList.remove('hidden')
    }
  }

  showSelectOptions() {
    if (this.hasSelectOptionsTarget) {
      this.selectOptionsTarget.classList.remove('hidden')
    }
    if (this.hasValuesInputTarget) this.valuesInputTarget.disabled = false
  }

  showBooleanOptions() {
    if (this.hasBooleanOptionsTarget) {
      this.booleanOptionsTarget.classList.remove('hidden')
    }
  }

  showMultiselectOptions() {
    if (this.hasMultiselectOptionsTarget) {
      this.multiselectOptionsTarget.classList.remove('hidden')
    }
    if (this.hasMultiselectValuesInputTarget) this.multiselectValuesInputTarget.disabled = false
  }

  showEntityOptions() {
    if (this.hasEntityOptionsTarget) {
      this.entityOptionsTarget.classList.remove('hidden')
    }
  }

  addValue(event) {
    event.preventDefault()
    this.addValueWithContent('')
    this.updateValues()
  }

  addValueWithContent(value) {
    const valueItem = document.createElement('div')
    valueItem.className = 'select-value-item flex items-center gap-2 mb-2'
    valueItem.innerHTML = `
      <input type="text" name="select_values[]" 
        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md" 
        data-custom-property-target="valueInput"
        data-action="input->custom-property#updateValues"
        value="${value}">
      <button type="button" class="text-red-600 hover:text-red-800" data-action="click->custom-property#removeValue">
        <i class="fas fa-trash"></i>
      </button>
    `
    this.valuesListTarget.appendChild(valueItem)
  }

  removeValue(event) {
    event.preventDefault()
    const valueItem = event.target.closest('.select-value-item')
    if (valueItem) {
      valueItem.remove()
      this.updateValues()
    }
  }

  updateValues() {
    const values = this.valueInputTargets.map(input => input.value.trim()).filter(v => v.length > 0)
    this.valuesInputTarget.value = JSON.stringify(values)
  }

  // Multiselect-spezifische Methoden
  initializeMultiselectValues() {
    const valuesInput = this.multiselectValuesInputTarget
    if (!valuesInput.value) return

    try {
      const values = JSON.parse(valuesInput.value)
      if (Array.isArray(values)) {
        this.multiselectValuesListTarget.innerHTML = ''
        values.forEach(value => this.addMultiselectValueWithContent(value))
      }
    } catch (e) {
      // Fehler beim Parsen der Werte
    }
  }

  addMultiselectValue(event) {
    event.preventDefault()
    this.addMultiselectValueWithContent('')
    this.updateMultiselectValues()
  }

  addMultiselectValueWithContent(value) {
    const valueItem = document.createElement('div')
    valueItem.className = 'multiselect-value-item flex items-center gap-2 mb-2'
    valueItem.innerHTML = `
      <input type="text" name="multiselect_values[]" 
        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md" 
        data-custom-property-target="multiselectValueInput"
        data-action="input->custom-property#updateMultiselectValues"
        value="${value}">
      <button type="button" class="text-red-600 hover:text-red-800" data-action="click->custom-property#removeMultiselectValue">
        <i class="fas fa-trash"></i>
      </button>
    `
    this.multiselectValuesListTarget.appendChild(valueItem)
  }

  removeMultiselectValue(event) {
    event.preventDefault()
    const valueItem = event.target.closest('.multiselect-value-item')
    if (valueItem) {
      valueItem.remove()
      this.updateMultiselectValues()
    }
  }

  updateMultiselectValues() {
    const values = this.multiselectValueInputTargets.map(input => input.value.trim()).filter(v => v.length > 0)
    this.multiselectValuesInputTarget.value = JSON.stringify(values)
  }
}